import React, { useEffect, useState, useContext } from 'react'
import { Row, Col, Table, Button, Form } from 'react-bootstrap'
import BackButton from '../../common/buttons/backButton'
import { useParams } from 'react-router'
import {
  getOrderDetail,
  updateOrderItemStatus
} from '../../../services/orderService'
import { Order } from '../../../models/Order'
import { toast } from 'react-toastify'
import { OrderItemStatus } from '../../../config/enums'
import { formatToPrice } from '../../../utility/prettifynumber'
import { friendlyOrderItemStatus } from '../../../utility/orderHelper'
import { friendlyMessageFor } from '../../../utility/errorHelper'
import { OrderItem } from '../../../models/Order'
import { VendorContext } from '../../../contexts/vendorContext'
import { Link } from 'react-router-dom'
import { IoIosCheckmark } from 'react-icons/io'
import styles from './orderDetail.module.css'
import constants from '../../../config/constants'
import Confirm from '../../common/modals/confirm'
import { FaShareSquare } from 'react-icons/fa'

const OrderDetail: React.FC = () => {
  const profile = useContext(VendorContext)!.profile
  const { orderId } = useParams()
  const [order, setOrder] = useState<Order | undefined>(undefined)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isStatusCanceled, setIsStatusCanceled] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const steps = [
    {
      status: OrderItemStatus.placed,
      count: 'One'
    },
    {
      status: OrderItemStatus.processing,
      count: 'Two'
    },
    {
      status: OrderItemStatus.disptached,
      count: 'Three'
    },
    {
      status: OrderItemStatus.delivered,
      count: 'Four'
    }
  ]

  useEffect(() => {
    getOrderDetail(orderId!)
      .then(orderDetail => {
        setOrder(orderDetail)
        setOrderItems(
          orderDetail!.orderItems.filter(o => o.vendor.id === profile!.vendorId)
        )
      })
      .catch(err => {
        console.error(err)
        toast.error(friendlyMessageFor(err), { autoClose: false })
      })
  }, [orderId, profile])

  useEffect(() => {
    if (orderItems.length === 0) {
      return
    }

    switch (orderItems[0].status) {
      case OrderItemStatus.placed:
      case OrderItemStatus.canceled:
        setCurrentStepIndex(0)
        setIsStatusCanceled(true)
        break
      case OrderItemStatus.processing:
        setCurrentStepIndex(1)
        break
      case OrderItemStatus.disptached:
        setCurrentStepIndex(2)
        break
      case OrderItemStatus.delivered:
        setCurrentStepIndex(3)
        break
    }
  }, [orderItems])

  const updateStatus = async (status: OrderItemStatus) => {
    if (status === orderItems[0].status) {
      return
    }

    const itemIds: string[] = []
    orderItems.forEach(oItem => {
      itemIds.push(oItem.item.id)
    })

    try {
      setIsProcessing(true)
      await updateOrderItemStatus(order!.id, itemIds, status)
      const orderItemsCopy = [...orderItems]
      orderItemsCopy.forEach(oItem => {
        oItem.status = status
      })
      setOrderItems(orderItemsCopy)
      setIsProcessing(false)
      toast.success(
        status === OrderItemStatus.canceled
          ? constants.alertMessage.orderCanceled
          : constants.alertMessage.orderStatusUpdated
      )
    } catch (err) {
      // console.error(err)
      toast.error(friendlyMessageFor(err), { autoClose: false })
      setIsProcessing(false)
    }
  }

  // updateStatus(OrderItemStatus.canceled)

  return (
    <div data-testid="orderDetailFC">
      <Confirm
        show={showConfirmModal}
        title="Confirm cancel order"
        body="Are you sure you want to cancel this order?"
        confirmText="Cancel Order"
        cancelText="Dismiss"
        onConfirmClick={() => {
          updateStatus(OrderItemStatus.canceled)
          setShowConfirmModal(false)
        }}
        onCancelClick={() => {
          setShowConfirmModal(false)
        }}
        isLoading={isProcessing}
        loadingText="Canceling..."
      />

      <Row className="pt-5 pb-3">
        <Col>
          <h1>
            <BackButton rootPaths={['/']} size={35} /> Order Detail
          </h1>
        </Col>
      </Row>

      {order && (
        <div data-testid="orderDetails">
          <Row>
            <Col lg={8}>
              <h3 className="mb-3">
                Order #: <span>{order?.orderNumber}</span>
              </h3>
              <div className="box">
                <Row>
                  <Col>
                    <h4>Customer</h4>
                    <p className="text-dark mt-4">
                      <span>
                        {order?.biller.firstName} {order?.biller.lastName}
                      </span>
                      <br />
                      <span>{order.biller.email}</span>
                    </p>
                    <p className="text-dark mb-0">{order.biller.phone}</p>
                  </Col>
                  <Col>
                    <h4>Recipient</h4>
                    <p className="text-dark mt-4">
                      <span>
                        {order?.recipient.firstName} {order?.recipient.lastName}
                      </span>
                      <br />
                      <span>{order.recipient.email}</span>
                    </p>
                    <p className="text-dark">{order.recipient.phone}</p>
                    <p className="text-dark mb-0">
                      <span>
                        {order.recipient.address.address1},{' '}
                        {order.recipient.address.address2}
                      </span>
                      <br />
                      <span>
                        {order.recipient.address.city},{' '}
                        {order.recipient.address.zipCode}
                      </span>
                      <br />
                      <span>
                        {order.recipient.address.region},{' '}
                        {order.recipient.address.country}
                      </span>
                      <br />
                    </p>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col>
                  <h3 className="mt-5">Order items</h3>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table className="styledTable">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty.</th>
                        <th>Subtotal</th>
                        <th>Shipping Fee</th>
                        <th>Total</th>
                        <th>View listing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.orderItems.map(oItem => {
                        return (
                          <tr key={oItem.item.id} data-testid="orderItemRow">
                            <td>
                              {oItem.item.name}
                              {oItem.gift && (
                                <p className="text-secondary">
                                  Free gift:{' '}
                                  <b>
                                    {oItem.gift.name}{' '}
                                    <a
                                      href={`/vendor/editgift/${oItem.gift.id}`}
                                      className="text-secondary"
                                    >
                                      <FaShareSquare />
                                    </a>
                                  </b>
                                </p>
                              )}
                            </td>
                            <td>{oItem.quantity}</td>
                            <td>
                              {formatToPrice(oItem.totalPrice, oItem.currency)}
                            </td>
                            <td>
                              {formatToPrice(
                                oItem.shippingInfo.fee,
                                oItem.currency
                              )}
                            </td>
                            <td>
                              {formatToPrice(
                                oItem.totalPrice + oItem.shippingInfo.fee,
                                oItem.currency
                              )}
                            </td>
                            <td>
                              <Link to={`/vendor/editlisting/${oItem.item.id}`}>
                                <Button>View</Button>
                              </Link>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Col>

            <Col lg={4}>
              <h3 className="mb-3">Status management</h3>
              <div className="box">
                {currentStepIndex === 4 ? (
                  <p>Order has been canceled.</p>
                ) : (
                  <>
                    <Form.Group controlId="formBasicCheckbox">
                      {steps.map((step, i) => {
                        return (
                          <Row
                            key={step.status}
                            className={`no-gutters ${styles.stepRow}`}
                            data-testid="statusStep"
                          >
                            <Col xs="auto" className="pr-4">
                              {i <= currentStepIndex ? (
                                <span
                                  className={`${styles.stepCircle} ${styles.done}`}
                                >
                                  <IoIosCheckmark size={80} />
                                </span>
                              ) : (
                                <span
                                  className={`${styles.stepCircle} ${styles.todo}`}
                                >
                                  {i + 1}
                                </span>
                              )}
                            </Col>
                            <Col className="align-v-center">
                              <div>
                                <div className="text-dark">
                                  Step {step.count}
                                </div>
                                <h4>{friendlyOrderItemStatus(step.status)}</h4>
                              </div>
                            </Col>
                            <Col xs="auto" className="align-v-center">
                              <Form.Check
                                type="checkbox"
                                checked={i <= currentStepIndex}
                                disabled={
                                  i <= 0 || isProcessing || isStatusCanceled
                                }
                                onChange={() => {
                                  updateStatus(step.status)
                                }}
                              />
                            </Col>
                          </Row>
                        )
                      })}
                    </Form.Group>
                    <div className="text-center">
                      {isStatusCanceled ? (
                        <p className="text-danger">Order canceled</p>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowConfirmModal(true)}
                          disabled={isProcessing}
                        >
                          Cancel order
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </div>
      )}
    </div>
  )
}

export default OrderDetail
